import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Search, BarChart3, Edit, X
} from 'lucide-react';
import { AreaChart, Area, PieChart as RechartsPieChart, Cell, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Pie } from 'recharts';

const Campaigns = ({ isDarkMode = true }) => {
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  // Filters array
  const filters = [
    { value: 'all', label: 'All Campaigns' },
    { value: 'active', label: 'Active' },
    { value: 'paused', label: 'Paused' },
    { value: 'completed', label: 'Completed' },
    { value: 'draft', label: 'Draft' }
  ];

  // Mock data
  const campaigns = [
    {
      id: 1,
      title: 'Summer Collection Launch',
      description: 'Promoting our new summer fashion line across social media platforms',
      status: 'active',
      platform: 'Instagram',
      budget: 15000,
      spent: 8900,
      impressions: 125000,
      clicks: 8900,
      conversions: 445,
      ctr: 7.12,
      cpc: 1.68,
      roas: 3.2,
      startDate: '2024-03-01',
      endDate: '2024-04-30',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop',
      performance: 94,
      trend: 'up'
    },
    {
      id: 2,
      title: 'Brand Awareness Campaign',
      description: 'Increasing brand visibility and recognition in target markets',
      status: 'paused',
      platform: 'Facebook',
      budget: 25000,
      spent: 18200,
      impressions: 89000,
      clicks: 5600,
      conversions: 280,
      ctr: 6.29,
      cpc: 3.25,
      roas: 2.1,
      startDate: '2024-02-15',
      endDate: '2024-05-15',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop',
      performance: 87,
      trend: 'down'
    },
    {
      id: 3,
      title: 'Product Showcase',
      description: 'Highlighting key product features and benefits',
      status: 'active',
      platform: 'TikTok',
      budget: 8000,
      spent: 4200,
      impressions: 210000,
      clicks: 15000,
      conversions: 750,
      ctr: 7.14,
      cpc: 0.28,
      roas: 4.8,
      startDate: '2024-03-10',
      endDate: '2024-04-10',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop',
      performance: 96,
      trend: 'up'
    },
    {
      id: 4,
      title: 'Holiday Special',
      description: 'Seasonal promotion for holiday shopping period',
      status: 'draft',
      platform: 'Multi-platform',
      budget: 30000,
      spent: 0,
      impressions: 0,
      clicks: 0,
      conversions: 0,
      ctr: 0,
      cpc: 0,
      roas: 0,
      startDate: '2024-12-01',
      endDate: '2024-12-31',
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop',
      performance: 0,
      trend: 'neutral'
    }
  ];

  const performanceData = [
    { name: 'Mon', impressions: 12000, clicks: 850, conversions: 42 },
    { name: 'Tue', impressions: 15000, clicks: 1100, conversions: 55 },
    { name: 'Wed', impressions: 18000, clicks: 1300, conversions: 65 },
    { name: 'Thu', impressions: 22000, clicks: 1600, conversions: 80 },
    { name: 'Fri', impressions: 25000, clicks: 1800, conversions: 90 },
    { name: 'Sat', impressions: 20000, clicks: 1400, conversions: 70 },
    { name: 'Sun', impressions: 16000, clicks: 1200, conversions: 60 },
  ];

  const platformData = [
    { name: 'Instagram', value: 45, color: '#E4405F' },
    { name: 'Facebook', value: 30, color: '#1877F2' },
    { name: 'TikTok', value: 20, color: '#000000' },
    { name: 'Twitter', value: 5, color: '#1DA1F2' },
  ];

  const filteredCampaigns = campaigns.filter(campaign => {
    const matchesFilter = activeFilter === 'all' || campaign.status === activeFilter;
    const matchesSearch = campaign.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         campaign.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return isDarkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700';
      case 'medium': return isDarkMode ? 'bg-yellow-500/20 text-yellow-300' : 'bg-yellow-100 text-yellow-700';
      case 'low': return isDarkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-700';
      default: return isDarkMode ? 'bg-gray-500/20 text-gray-300' : 'bg-gray-100 text-gray-700';
    }
  };

  const CampaignCard = ({ campaign }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      onClick={() => setSelectedCampaign(campaign)}
      className="bg-black/20 backdrop-blur-xl rounded-2xl overflow-hidden shadow-2xl border border-white/10 hover:shadow-3xl transition-all duration-300 cursor-pointer"
    >
      <div className="relative h-48 bg-gradient-to-br from-gray-800 to-gray-900">
        <img 
          src={campaign.image} 
          alt={campaign.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute top-3 right-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            campaign.status === 'active' 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
              : campaign.status === 'paused'
              ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
              : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
          }`}>
            {campaign.status}
          </span>
        </div>
        <div className="absolute bottom-3 left-3">
          <span className="px-2 py-1 bg-black/50 backdrop-blur-sm text-white text-xs rounded-lg border border-white/20">
            {campaign.platform}
          </span>
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="font-semibold text-white mb-2">{campaign.title}</h3>
        <p className="text-sm text-gray-300 mb-4 line-clamp-2">{campaign.description}</p>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-300">Performance</span>
            <div className="flex items-center space-x-2">
              <span className="font-semibold text-white">{campaign.performance}%</span>
              {campaign.trend === 'up' ? (
                <BarChart3 className="h-4 w-4 text-green-400" />
              ) : (
                <BarChart3 className="h-4 w-4 text-red-400" />
              )}
            </div>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-300">Engagement</span>
            <span className="font-medium text-white">{campaign.engagement}</span>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-300">Reach</span>
            <span className="font-medium text-white">{campaign.reach ? campaign.reach.toLocaleString() : '0'}</span>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-300">Budget</span>
            <span className="font-medium text-white">${campaign.budget ? campaign.budget.toLocaleString() : '0'}</span>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">
              {new Date(campaign.startDate).toLocaleDateString()} - {new Date(campaign.endDate).toLocaleDateString()}
            </span>
            <div className="flex space-x-2">
              <button className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-300">
                <X className="h-4 w-4" />
              </button>
              <button className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-300">
                <Edit className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  const CampaignDetail = ({ campaign, onClose }) => {
    if (!campaign) return null;

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white">{campaign.title}</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors text-gray-400 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Campaign Info */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Campaign Details</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-400">Description</p>
                  <p className="text-white">{campaign.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-400">Platform</p>
                    <p className="font-medium text-white">{campaign.platform}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Status</p>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      campaign.status === 'active' 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : campaign.status === 'paused'
                        ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                        : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                    }`}>
                      {campaign.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Start Date</p>
                    <p className="font-medium text-white">{campaign.startDate}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">End Date</p>
                    <p className="font-medium text-white">{campaign.endDate}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Performance Metrics</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-sm text-gray-400">CTR</p>
                  <p className="text-lg font-bold text-white">{campaign.ctr}%</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-sm text-gray-400">CPC</p>
                  <p className="text-lg font-bold text-white">SAR {campaign.cpc}</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-sm text-gray-400">ROAS</p>
                  <p className="text-lg font-bold text-white">{campaign.roas}x</p>
                </div>
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-sm text-gray-400">Performance</p>
                  <p className="text-lg font-bold text-white">{campaign.performance}%</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Charts */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Performance Trend</h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={performanceData}>
                  <defs>
                    <linearGradient id="colorImpressions" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.1)', 
                      borderRadius: '12px',
                      color: '#fff'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="impressions" 
                    stroke="#8B5CF6" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorImpressions)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-white mb-3">Platform Distribution</h3>
              <ResponsiveContainer width="100%" height={150}>
                <RechartsPieChart>
                  <Pie
                    data={platformData}
                    cx="50%"
                    cy="50%"
                    innerRadius={30}
                    outerRadius={60}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {platformData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(0, 0, 0, 0.9)', 
                      border: '1px solid rgba(255, 255, 255, 0.1)', 
                      borderRadius: '12px',
                      color: '#fff'
                    }}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-white/10">
          <button className="px-4 py-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors">
            Edit Campaign
          </button>
          <button className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-all duration-300">
            View Full Report
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full h-full px-8 py-8 space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0"
      >
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
            Marketing Campaigns
          </h1>
          <p className="text-gray-300 mt-2 text-lg">
            Manage and track all your marketing campaigns in one place
          </p>
        </div>
        <div className="flex space-x-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center space-x-2"
          >
            <Plus className="h-5 w-5" />
            <span>New Campaign</span>
          </motion.button>
        </div>
      </motion.div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-black/20 backdrop-blur-xl rounded-2xl p-6 shadow-2xl border border-white/10"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex flex-wrap gap-3">
            {filters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setActiveFilter(filter.value)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                  activeFilter === filter.value
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20 hover:text-white'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search campaigns..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full lg:w-80 pl-10 pr-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50"
            />
          </div>
        </div>
      </motion.div>

      {/* Campaigns Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {filteredCampaigns.map((campaign, index) => (
          <motion.div
            key={campaign.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 + index * 0.1 }}
          >
            <CampaignCard campaign={campaign} />
          </motion.div>
        ))}
      </motion.div>

      {/* Campaign Detail Modal */}
      <AnimatePresence>
        {selectedCampaign && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedCampaign(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-black/90 backdrop-blur-xl rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-white/10"
            >
              <CampaignDetail campaign={selectedCampaign} onClose={() => setSelectedCampaign(null)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Campaigns; 